<template>
  <div class="home-page">
    <el-card class="home-panel" shadow="never" :body-style="{ padding: '0', height: '100%' }">
      <div class="home-heading">
        <div class="home-heading-copy">
          <span class="home-eyebrow">RECENTLY ADDED</span>
          <div class="home-title-line">
            <h1>最新收录</h1>
            <el-button class="home-refresh" size="mini" circle icon="el-icon-refresh" :loading="loading" aria-label="刷新首页作品" title="刷新" @click="loadPage(currentPage)"></el-button>
            <span v-if="total" class="home-count">{{ total.toLocaleString() }} 部作品</span>
          </div>
        </div>
        <div class="home-actions">
          <el-button size="small" icon="el-icon-search" @click="$router.push('/table')">浏览目录</el-button>
          <el-button size="small" icon="el-icon-user" @click="$router.push('/maker')">浏览社团</el-button>
        </div>
      </div>

      <app-scrollbar ref="gameScroll" class="home-scroll" v-loading="loading">
        <div v-if="games.length" class="home-grid">
          <el-card v-for="game in games" :key="game.id" class="home-work-card" shadow="hover" :body-style="{ padding: '0' }">
            <div class="home-work-inner">
              <button class="home-cover" type="button" :aria-label="`查看${game.name}的样图`" @click="openSamples(game)">
                <el-image :src="game.cover" fit="contain" lazy>
                  <div slot="error" class="home-image-state"><i class="el-icon-picture-outline"></i></div>
                  <div slot="placeholder" class="home-image-state"><i class="el-icon-loading"></i></div>
                </el-image>
                <el-tag v-if="game.deleted" class="home-deleted-badge" size="mini" type="danger" effect="dark">已下架</el-tag>
                <span class="home-cover-hint"><i class="el-icon-picture-outline"></i> 样图</span>
              </button>
              <div class="home-work-info">
                <div class="home-work-ids">
                  <button class="home-work-id" type="button" :aria-label="`复制${game.id}`" title="点击复制站内 ID" @click="copyText(game.id)">{{ game.id }}</button>
                  <button class="home-work-id" type="button" :aria-label="`复制${game.gameId}`" title="点击复制 RJ 号" @click="copyText(game.gameId)">{{ game.gameId }}</button>
                </div>
                <div class="home-work-title-row">
                  <el-tooltip :content="game.name" placement="top" :open-delay="550">
                    <a v-if="game.dlsiteUrl && !game.deleted" class="home-work-title" :href="game.dlsiteUrl" target="_blank" rel="noopener noreferrer">{{ game.name }}</a>
                    <span v-else class="home-work-title">{{ game.name }}</span>
                  </el-tooltip>
                  <el-button class="home-copy-button" type="text" icon="el-icon-document-copy" :aria-label="`复制标题：${game.name}`" title="复制标题" @click="copyText(game.name)"></el-button>
                </div>
                <div class="home-work-author">
                  <a v-if="game.dlsiteMakerUrl" :href="game.dlsiteMakerUrl" target="_blank" rel="noopener noreferrer">{{ game.author }}</a>
                  <span v-else>{{ game.author }}</span>
                  <el-button v-if="game.author" class="home-copy-button" type="text" icon="el-icon-document-copy" :aria-label="`复制社团：${game.author}`" title="复制社团" @click="copyText(game.author)"></el-button>
                </div>
                <div class="home-work-bottom">
                  <div class="home-tags">
                    <el-tag v-if="game.type" size="mini" type="success">{{ game.type.split('/')[0] }}</el-tag>
                    <el-tag v-if="game.language" size="mini" type="info">{{ game.language }}</el-tag>
                  </div>
                  <span v-if="game.score" class="home-score"><i class="el-icon-star-on"></i> {{ Number(game.score).toFixed(2) }}</span>
                </div>
              </div>
            </div>
          </el-card>
        </div>
        <el-empty v-else-if="!loading" description="暂无收录作品"></el-empty>
      </app-scrollbar>

      <div class="home-footer">
        <span class="home-page-label">第 {{ currentPage }} / {{ totalPage || 1 }} 页</span>
        <el-pagination
          background
          layout="prev, pager, next"
          :current-page="currentPage"
          :page-count="totalPage || 1"
          :pager-count="pagerCount"
          :disabled="loading"
          @current-change="loadPage"
        ></el-pagination>
      </div>
    </el-card>

    <el-dialog
      :visible.sync="showSamples"
      :width="dialogWidth"
      :show-close="false"
      :close-on-click-modal="true"
      append-to-body
      class="home-sample-dialog"
    >
      <template slot="title">
        <div v-if="selectedGame" class="sample-heading">
          <div class="sample-heading-main">
            <span class="sample-eyebrow">作品样图</span>
            <div class="sample-code-row">
              <button class="sample-id" type="button" :aria-label="`复制${selectedGame.id}`" title="点击复制站内 ID" @click="copyText(selectedGame.id)">{{ selectedGame.id }}</button>
              <button class="sample-id" type="button" :aria-label="`复制${selectedGame.gameId}`" title="点击复制 RJ 号" @click="copyText(selectedGame.gameId)">{{ selectedGame.gameId }}</button>
              <el-tag v-if="selectedGame.deleted" size="mini" type="danger" effect="dark">已下架</el-tag>
            </div>
            <h2>{{ selectedGame.name }}</h2>
          </div>
          <div class="sample-heading-actions">
            <el-button v-if="selectedGame.dlsiteUrl && !selectedGame.deleted" size="mini" type="primary" plain icon="el-icon-link" @click="openWorkLink">DLsite</el-button>
            <el-button class="sample-close" size="mini" icon="el-icon-close" circle aria-label="关闭预览" @click="showSamples = false"></el-button>
          </div>
        </div>
      </template>
      <div v-if="showSamples" class="sample-viewer">
        <div class="sample-stage">
          <el-carousel ref="sampleCarousel" :key="selectedGame && selectedGame.id" :height="carouselHeight" indicator-position="none" arrow="never" :autoplay="false" @change="onSampleChange">
            <el-carousel-item v-for="(sample, index) in sampleList" :key="`${sample}-${index}`">
              <el-image :src="sample" fit="contain" class="sample-stage-image">
                <div slot="error" class="sample-image-error"><i class="el-icon-picture-outline"></i><span>图片加载失败</span></div>
                <div slot="placeholder" class="sample-image-error"><i class="el-icon-loading"></i><span>正在加载</span></div>
              </el-image>
            </el-carousel-item>
          </el-carousel>
          <el-button v-if="sampleList.length > 1" class="sample-arrow sample-arrow-prev" icon="el-icon-arrow-left" circle aria-label="上一张" @click="changeSample(-1)"></el-button>
          <el-button v-if="sampleList.length > 1" class="sample-arrow sample-arrow-next" icon="el-icon-arrow-right" circle aria-label="下一张" @click="changeSample(1)"></el-button>
          <span class="sample-counter">{{ activeSample + 1 }} / {{ sampleList.length }}</span>
        </div>
        <div class="sample-filmstrip" aria-label="选择样图">
          <button
            v-for="(sample, index) in sampleList"
            :key="`thumb-${sample}-${index}`"
            ref="sampleThumbs"
            type="button"
            class="sample-thumb"
            :class="{ 'is-active': activeSample === index }"
            :aria-label="index === 0 ? '查看封面' : `查看第${index + 1}张样图`"
            :aria-pressed="activeSample === index"
            @click="selectSample(index)"
          >
            <el-image :src="sample" fit="contain"></el-image>
            <span>{{ index === 0 ? '封面' : String(index + 1).padStart(2, '0') }}</span>
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AppScrollbar from '@/components/AppScrollbar.vue'

export default {
  name: 'HomeNew',
  components: { AppScrollbar },
  data() {
    return {
      games: [],
      loading: false,
      total: 0,
      totalPage: 0,
      currentPage: 1,
      viewportWidth: window.innerWidth,
      selectedGame: null,
      sampleList: [],
      showSamples: false,
      activeSample: 0,
      requestId: 0
    }
  },
  computed: {
    pagerCount() {
      return this.viewportWidth < 768 ? 5 : 7
    },
    dialogWidth() {
      return this.viewportWidth < 768 ? 'calc(100% - 20px)' : 'min(1080px, calc(100% - 48px))'
    },
    carouselHeight() {
      return this.viewportWidth < 768 ? '46dvh' : 'min(62dvh, 650px)'
    }
  },
  created() {
    this.loadPage(1)
  },
  mounted() {
    window.addEventListener('resize', this.updateViewport)
    window.addEventListener('keydown', this.onPreviewKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport)
    window.removeEventListener('keydown', this.onPreviewKeydown)
    this.requestId += 1
  },
  methods: {
    updateViewport() {
      this.viewportWidth = window.innerWidth
    },
    async loadPage(pageNo) {
      if (this.loading || pageNo < 1) return
      const requestId = ++this.requestId
      this.loading = true
      try {
        const response = await axios.get('/api/open/home', { params: { pageNo } })
        if (requestId !== this.requestId) return
        if (response.data.code !== 200) throw new Error(response.data.message || '加载失败')
        const page = response.data.data || {}
        this.games = page.rows || []
        this.total = Number(page.total || 0)
        this.totalPage = Number(page.pageCount || 0)
        this.currentPage = Number(page.current || pageNo)
        this.$nextTick(() => {
          if (this.$refs.gameScroll) this.$refs.gameScroll.$el.scrollTop = 0
        })
      } catch (error) {
        if (requestId === this.requestId) this.$message.error('作品加载失败，请稍后重试')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    openSamples(game) {
      this.selectedGame = game
      this.sampleList = game.sample && game.sample.length ? game.sample : [game.cover]
      this.activeSample = 0
      this.showSamples = true
      this.$nextTick(() => {
        if (this.$refs.sampleCarousel) this.$refs.sampleCarousel.setActiveItem(0)
      })
    },
    openWorkLink() {
      if (this.selectedGame && this.selectedGame.dlsiteUrl) {
        window.open(this.selectedGame.dlsiteUrl, '_blank', 'noopener,noreferrer')
      }
    },
    changeSample(delta) {
      if (this.sampleList.length < 2) return
      this.selectSample((this.activeSample + delta + this.sampleList.length) % this.sampleList.length)
    },
    selectSample(index) {
      this.activeSample = index
      if (this.$refs.sampleCarousel) this.$refs.sampleCarousel.setActiveItem(index)
    },
    onSampleChange(index) {
      this.activeSample = index
      this.$nextTick(() => {
        const thumb = this.$refs.sampleThumbs && this.$refs.sampleThumbs[index]
        if (thumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      })
    },
    onPreviewKeydown(event) {
      if (!this.showSamples || event.altKey || event.ctrlKey || event.metaKey) return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.changeSample(-1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.changeSample(1)
      }
    },
    async copyText(value) {
      const text = String(value)
      try {
        if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard API unavailable')
        await navigator.clipboard.writeText(text)
      } catch (error) {
        const input = document.createElement('textarea')
        input.value = text
        input.setAttribute('readonly', '')
        input.style.position = 'fixed'
        input.style.opacity = '0'
        document.body.appendChild(input)
        let copied = false
        try {
          input.select()
          input.setSelectionRange(0, input.value.length)
          copied = document.execCommand('copy')
        } catch (copyError) {
          copied = false
        } finally {
          document.body.removeChild(input)
        }
        if (!copied) {
          this.$message.error('复制失败，请手动复制')
          return
        }
      }
      this.$message.success('已复制')
    }
  }
}
</script>

<style scoped>
.home-page, .home-panel { height: 100%; min-height: 0; }
.home-panel { border: 1px solid rgba(220, 223, 230, .72); border-radius: 17px; background: rgba(255, 255, 255, .58); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); box-shadow: 0 12px 28px rgba(48, 49, 51, .08); overflow: hidden; }
.home-panel /deep/ .el-card__body { display: flex; flex-direction: column; min-height: 0; }
.home-heading { flex: none; display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 22px 24px 18px; border-bottom: 1px solid rgba(220, 223, 230, .72); }
.home-eyebrow { color: #409eff; font-size: 10px; font-weight: 700; letter-spacing: .18em; }
.home-title-line { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin-top: 6px; }
.home-title-line h1 { margin: 0; color: #303133; font-size: 24px; line-height: 1.3; }
.home-refresh { padding: 6px; }
.home-count { color: #909399; font-size: 12px; }
.home-actions { display: flex; gap: 8px; }
.home-actions .el-button + .el-button { margin-left: 0; }
.home-scroll { flex: 1; min-height: 0; padding: 20px 24px; }
.home-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.home-work-card { min-width: 0; border: 1px solid rgba(220, 223, 230, .78); border-radius: 12px; background: rgba(255, 255, 255, .53); overflow: hidden; }
.home-work-inner { display: flex; gap: 12px; min-width: 0; padding: 11px; }
.home-cover { position: relative; display: block; flex: 0 0 132px; width: 132px; height: 99px; padding: 0; border: 0; border-radius: 7px; overflow: hidden; background: rgba(245, 247, 250, .65); cursor: pointer; }
.home-cover /deep/ .el-image { width: 100%; height: 100%; }
.home-deleted-badge { position: absolute; top: 5px; left: 5px; z-index: 1; border: 0; }
.home-image-state { display: grid; place-items: center; width: 100%; height: 100%; color: #909399; background: rgba(245, 247, 250, .6); }
.home-cover-hint { position: absolute; right: 4px; bottom: 4px; padding: 2px 5px; border-radius: 3px; background: rgba(48, 49, 51, .67); color: white; font-size: 10px; opacity: 0; transition: opacity .15s; }
.home-cover:hover .home-cover-hint, .home-cover:focus-visible .home-cover-hint { opacity: 1; }
.home-work-info { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.home-work-ids { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-bottom: 3px; }
.home-work-id { display: inline-flex; align-items: center; max-width: 100%; min-height: 19px; padding: 0 6px; border: 1px solid rgba(64, 158, 255, .22); border-radius: 5px; background: rgba(64, 158, 255, .08); color: #409eff; font-family: inherit; font-size: 10px; font-weight: 700; line-height: 16px; white-space: nowrap; cursor: pointer; }
.home-work-id:hover, .home-work-id:focus-visible { border-color: #409eff; background: rgba(64, 158, 255, .16); }
.home-work-title-row { display: flex; align-items: flex-start; min-width: 0; gap: 3px; }
.home-copy-button { flex: none; padding: 1px 2px; font-size: 12px; line-height: 16px; }
.home-work-title { display: -webkit-box; min-width: 0; max-height: 38px; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; color: #303133; font-size: 13px; line-height: 19px; font-weight: 600; text-decoration: none; }
a.home-work-title:hover, .home-work-author a:hover { color: #409eff; }
.home-work-author { display: flex; align-items: center; min-width: 0; margin-top: 3px; color: #909399; font-size: 11px; }
.home-work-author a, .home-work-author > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.home-work-author a { color: #606266; text-decoration: none; }
.home-work-bottom { display: flex; justify-content: space-between; align-items: flex-end; gap: 5px; margin-top: auto; padding-top: 6px; }
.home-tags { display: flex; flex-wrap: wrap; gap: 4px; min-width: 0; }
.home-tags .el-tag { max-width: 90px; overflow: hidden; text-overflow: ellipsis; }
.home-score { flex: none; color: #e6a23c; font-size: 12px; font-weight: 700; white-space: nowrap; }
.home-footer { flex: none; display: flex; justify-content: space-between; align-items: center; gap: 10px; min-height: 56px; padding: 9px 24px; border-top: 1px solid rgba(220, 223, 230, .72); }
.home-page-label { color: #909399; font-size: 11px; white-space: nowrap; }
.home-footer /deep/ .el-pagination { padding: 0; }
@media (min-width: 1750px) { .home-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } .home-cover { flex-basis: 152px; width: 152px; height: 114px; } }
@media (max-width: 1050px) { .home-grid { grid-template-columns: 1fr; } }
@media (max-width: 767px) {
  .home-heading { align-items: flex-start; padding: 14px 12px 12px; }
  .home-title-line h1 { font-size: 19px; }
  .home-count { font-size: 10px; }
  .home-actions { flex-direction: column; gap: 5px; }
  .home-actions .el-button { padding: 6px 8px; font-size: 11px; }
  .home-scroll { padding: 10px; }
  .home-grid { gap: 9px; }
  .home-work-inner { gap: 10px; padding: 9px; }
  .home-cover { flex-basis: 112px; width: 112px; height: 84px; }
  .home-work-title { font-size: 12px; line-height: 17px; max-height: 34px; }
  .home-work-author { font-size: 10px; }
  .home-footer { justify-content: center; min-height: 48px; padding: 7px 5px; }
  .home-page-label { display: none; }
  .home-footer /deep/ .el-pagination .el-pager li, .home-footer /deep/ .el-pagination button { min-width: 27px; margin: 0 2px; }
}
</style>

<style>
.home-sample-dialog .el-dialog { margin-top: 4vh !important; max-height: 92vh; max-height: 92dvh; overflow: hidden; border: 1px solid rgba(220, 223, 230, .8); border-radius: 17px; background: rgba(255, 255, 255, .9); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); box-shadow: 0 24px 70px rgba(16, 24, 40, .28); }
.home-sample-dialog .el-dialog__header { padding: 18px 22px 15px; border-bottom: 1px solid rgba(220, 223, 230, .8); }
.home-sample-dialog .el-dialog__body { padding: 0; }
.sample-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; min-width: 0; }
.sample-heading-main { min-width: 0; }
.sample-eyebrow { color: #409eff; font-size: 10px; font-weight: 700; letter-spacing: .12em; }
.sample-code-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 4px; color: #409eff; font-size: 11px; font-weight: 700; }
.sample-id { display: inline-flex; align-items: center; max-width: 100%; padding: 2px 7px; border: 1px solid rgba(64, 158, 255, .22); border-radius: 5px; background: rgba(64, 158, 255, .08); color: #409eff; font: inherit; white-space: nowrap; cursor: pointer; }
.sample-id:hover, .sample-id:focus-visible { border-color: #409eff; background: rgba(64, 158, 255, .16); }
.sample-code-row .el-tag { margin-left: 4px; }
.sample-heading h2 { display: -webkit-box; max-width: 780px; max-height: 46px; margin: 4px 0 0; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; color: #303133; font-size: 17px; line-height: 23px; font-weight: 600; }
.sample-heading-actions { display: flex; flex: none; gap: 8px; align-items: center; padding-top: 8px; }
.sample-heading-actions .el-button + .el-button { margin-left: 0; }
.sample-stage { position: relative; overflow: hidden; background: #171c26; }
.sample-stage .el-carousel__item { display: flex; align-items: center; justify-content: center; }
.sample-stage-image { width: 100%; height: 100%; }
.sample-stage-image .el-image__inner { width: 100%; height: 100%; }
.sample-image-error { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 100%; color: #c0c4cc; font-size: 13px; }
.sample-image-error i { font-size: 25px; }
.sample-arrow { position: absolute; top: 50%; z-index: 2; transform: translateY(-50%); border: 1px solid rgba(255, 255, 255, .58); background: rgba(255, 255, 255, .84); color: #409eff; }
.sample-arrow:hover, .sample-arrow:focus { background: #fff; color: #409eff; }
.sample-arrow-prev { left: 16px; }
.sample-arrow-next { right: 16px; }
.sample-counter { position: absolute; right: 14px; bottom: 12px; z-index: 2; padding: 5px 10px; border-radius: 12px; background: rgba(18, 24, 34, .72); color: #fff; font-size: 12px; font-variant-numeric: tabular-nums; }
.sample-filmstrip { display: flex; gap: 9px; padding: 11px 18px 13px; overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: thin; scrollbar-color: #409eff rgba(220, 223, 230, .5); }
.sample-filmstrip::-webkit-scrollbar { height: 6px; }
.sample-filmstrip::-webkit-scrollbar-thumb { border-radius: 6px; background: #409eff; }
.sample-thumb { display: flex; flex: 0 0 94px; flex-direction: column; gap: 3px; width: 94px; padding: 4px; border: 2px solid transparent; border-radius: 8px; background: rgba(245, 247, 250, .88); color: #909399; font: inherit; font-size: 10px; cursor: pointer; }
.sample-thumb.is-active { border-color: #409eff; background: rgba(64, 158, 255, .12); color: #409eff; }
.sample-thumb .el-image { display: block; width: 100%; height: 60px; }
.sample-thumb span { align-self: center; }
@media (max-width: 767px) {
  .home-sample-dialog .el-dialog { margin-top: 3vh !important; margin-top: 3dvh !important; max-height: 94dvh; }
  .home-sample-dialog .el-dialog__header { padding: 12px 12px 10px; }
  .sample-heading { gap: 8px; }
  .sample-heading h2 { max-height: 38px; font-size: 13px; line-height: 19px; }
  .sample-heading-actions { gap: 4px; padding-top: 1px; }
  .sample-heading-actions .el-button { padding: 6px; }
  .sample-heading-actions .el-button:first-child { font-size: 10px; }
  .sample-arrow { padding: 8px; }
  .sample-arrow-prev { left: 6px; }
  .sample-arrow-next { right: 6px; }
  .sample-counter { right: 8px; bottom: 8px; padding: 3px 7px; font-size: 10px; }
  .sample-filmstrip { gap: 6px; padding: 8px 9px 10px; }
  .sample-thumb { flex-basis: 68px; width: 68px; padding: 3px; }
  .sample-thumb .el-image { height: 42px; }
}
</style>
